import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { bookingService } from '../api/services/bookingService'
import type { Vehicle } from '../types/booking'
type FleetVehicleCardProps = {
  category: string
  name: string
  description?: string
  image: string
  passengers: number
  luggage: number
  features?: string[]
  onClick?: () => void
  variant?: 'grid' | 'compact'
}

function FleetVehicleCard({
  category,
  name,
  description = "Comfortable and modern vehicle for your journey.",
  image,
  passengers,
  luggage,
  features = [],
  onClick,
  variant = 'grid',
}: FleetVehicleCardProps) {
  const isCompact = variant === 'compact'

  const imageAdjustment =
    name === 'VW Passat'
      ? 'scale-[1.62] translate-y-[10px]'
      : name === 'Mercedes V-Class'
        ? 'scale-[1.08]'
        : 'scale-[1.02]'

  return (
    <article
      onClick={onClick}
      className={`flex h-full w-full flex-col overflow-hidden rounded-[24px] bg-[#EBEBEB] shadow-[0_1px_18px_rgba(0,0,0,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_5px_24px_rgba(0,0,0,0.10)] sm:bg-[#ECECEC] ${
        onClick ? 'cursor-pointer' : ''
      }`}
    >
      {/* Green vehicle area */}
      <div
        className={`relative w-full flex-shrink-0 rounded-b-[20px] bg-[#BAD67E] sm:bg-[#BFD86E] ${
          isCompact ? 'h-[285px] sm:h-[280px]' : 'h-[285px]'
        }`}
      >
        <div className="absolute left-5 top-4 z-10 inline-flex h-[36px] items-center gap-2 rounded-[12px] bg-[#96A86C] px-[14px] shadow-[0_1px_6px_rgba(0,0,0,0.08)] sm:h-auto sm:bg-[#9FB26D] sm:py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#CDCDCD]" />
          <span className="font-host text-[13px] font-medium leading-none text-[#111111]">
            {category}
          </span>
        </div>

        <div
          className={`flex h-full w-full items-center justify-center overflow-hidden px-4 ${
            isCompact ? 'pb-2 pt-11' : 'pb-2 pt-12'
          }`}
        >
          <img
            src={image}
            alt={name}
            className={`w-full object-contain drop-shadow-[0_15px_16px_rgba(0,0,0,0.22)] ${
              isCompact
                ? `max-h-[210px] ${imageAdjustment}`
                : `max-h-[215px] ${imageAdjustment}`
            }`}
          />
        </div>
      </div>

      {/* Information area */}
      <div className="flex min-h-0 flex-1 flex-col px-[40px] pb-4 pt-4 sm:px-5">
        <h3 className="font-host text-[18px] font-semibold leading-[1.15] text-[#252525]">
          {name}
        </h3>

        <p className="mt-2 min-h-[38px] line-clamp-3 font-host text-[13px] leading-[1.42] text-[#737373]">
  {description}
</p>

        {/* Capacity: people icon + luggage icon */}
        <div className="mt-3 flex items-center gap-2 text-[#3D3D3D]">
          <span className="text-[12px] leading-none text-[#777777]">•</span>

          <img
            src="/assets/images/icon-adult.png"
            alt="Passengers"
            className="h-[16px] w-[16px] object-contain opacity-75"
          />

          <span className="font-host text-[13px] font-medium text-[#555555]">
            {passengers}
          </span>

          <span className="text-[12px] leading-none text-[#777777]">•</span>

          <img
            src="/assets/images/icon-child.png"
            alt="Luggage"
            className="h-[16px] w-[16px] object-contain opacity-75"
          />

          <span className="font-host text-[13px] font-medium text-[#555555]">
            {luggage}
          </span>
        </div>

        <div className="mt-3 flex flex-col gap-2">
          {features.map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <span
                className="h-[15px] w-[15px] flex-shrink-0 bg-[#424242]"
                style={{
                  clipPath:
                    'polygon(50% 0%,61% 13%,78% 8%,84% 25%,100% 32%,92% 50%,100% 68%,84% 75%,78% 92%,61% 87%,50% 100%,39% 87%,22% 92%,16% 75%,0% 68%,8% 50%,0% 32%,16% 25%,22% 8%,39% 13%)',
                }}
              />

              <span className="font-host text-[13px] font-medium text-[#555555]">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>
    </article>
  )
}

type DropdownProps = {
  label: string
  value: string
  options: string[]
  onChange: (val: string) => void
}

function Dropdown({ label, value, options, onChange }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative w-full flex-1">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex min-h-[114px] w-full items-center justify-between rounded-[10px] border border-[#A5A5A5] bg-white px-[30px] py-5 text-left shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-colors hover:border-[#747474] sm:px-7"
      >
        <span className="flex flex-col items-start">
          <span className="font-poppins text-[12px] font-medium uppercase tracking-[0.01em] text-[#111111]">
            {label}
          </span>

          <span className="mt-2 font-host text-[20px] font-semibold leading-none text-[#111111]">
            {value}
          </span>
        </span>

        <svg
          width="24"
          height="16"
          viewBox="0 0 24 16"
          fill="none"
          className={`hidden shrink-0 text-black transition-transform duration-200 sm:block ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        >
          <path
            d="M2 2L12 12L22 2"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            aria-label="Close dropdown"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setIsOpen(false)}
          />

          <ul className="absolute left-0 right-0 z-50 mt-2 max-h-[240px] overflow-y-auto rounded-[14px] border border-[#E5E5E5] bg-white py-2 shadow-[0_7px_24px_rgba(0,0,0,0.12)]">
            {options.map((option) => (
              <li key={option}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option)
                    setIsOpen(false)
                  }}
                  className={`w-full px-7 py-3 text-left font-host text-[15px] font-medium transition-colors hover:bg-slate-50 ${
                    value === option
                      ? 'bg-[#FF7A29]/5 font-semibold text-[#FF7A29]'
                      : 'text-[#333333]'
                  }`}
                >
                  {option}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function getVehicleOverview(_category: string, _name: string, description?: string) {
  return description ?? ''
}

function getMobileVehicleOverview(category: string, name: string, description?: string) {
  const cat = category.toLowerCase()

  if (cat.includes('saloon')) {
    return `The ${category} is ideal for couples, small families, and business travelers seeking comfortable and reliable transportation. It offers spacious seating, climate control, and ample luggage capacity for airport transfers and city-to-city travel.`
  }

  return getVehicleOverview(category, name, description)
}

export default function Fleets() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [selectedType, setSelectedType] = useState('Any Type')
  const [selectedMake, setSelectedMake] = useState('Any Make')
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
  const [fleetVehicles, setFleetVehicles] = useState<Vehicle[]>([])

  useEffect(() => {
  bookingService.fetchVehicles().then((vehicles) => {
    console.log('FETCHED FLEET VEHICLES:', vehicles)
    setFleetVehicles(vehicles)
  })
}, [])
  
  // Set selected vehicle from query param on initial mount or when fleet loads
  useEffect(() => {
    if (fleetVehicles.length > 0) {
      const vehicleName = searchParams.get('vehicle')
      if (vehicleName) {
        const vehicle = fleetVehicles.find(v => v.name.toLowerCase() === vehicleName.toLowerCase())
        if (vehicle) setSelectedVehicle(vehicle)
      }
    }
  }, [searchParams, fleetVehicles])

  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -384 : 384
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  // Scroll to top when a vehicle is selected or navigated back
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [selectedVehicle])

  const vehicleTypes = ['Any Type', 'Saloon', 'Van', 'Minibus']
  const vehicleMakes = ['Any Make', 'VW', 'Mercedes', 'Renault']

  const filteredVehicles = fleetVehicles.filter((vehicle) => {
    if (selectedType !== 'Any Type') {
      const typeLower = selectedType.toLowerCase()
      const categoryLower = vehicle.category.toLowerCase()
      if (typeLower === 'saloon') {
        if (!categoryLower.includes('saloon')) return false
      } else if (typeLower === 'van') {
        if (!categoryLower.includes('van')) return false
      } else if (typeLower === 'minibus') {
        if (!categoryLower.includes('minibus')) return false
      }
    }

    if (selectedMake !== 'Any Make') {
      const makeLower = selectedMake.toLowerCase()
      const nameLower = vehicle.name.toLowerCase()
      if (!nameLower.startsWith(makeLower)) return false
    }

    return true
  })

  // Exclude current vehicle in exploration carousel
  const otherVehicles = fleetVehicles.filter(
    (vehicle) =>
      !selectedVehicle || vehicle.name !== selectedVehicle.name,
  )

  const detailPassengers = selectedVehicle?.passengers ?? 0
  const detailSuitcases = selectedVehicle?.luggage ?? 0
  const specificationSuitcases = selectedVehicle?.luggage ?? 0

  const whatsIncludedItems = selectedVehicle?.features || []

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {selectedVehicle ? (
        /* ================= DETAIL VIEW ================= */
        <>
          {/* ================= MOBILE DETAIL VIEW ================= */}
          <div className="block w-full bg-white sm:hidden">
            {/* Mobile Navbar */}
            <div className="relative z-50 bg-white px-4">
              <Header />
            </div>

            <main className="relative w-full px-[17px] pb-20 pt-[20px]">
              {/* Back */}
              <button
                type="button"
                onClick={() => setSelectedVehicle(null)}
                className="flex h-[36px] items-center gap-[7px] rounded-full border border-[#D8D8D8] bg-white px-[15px] font-host text-[13px] font-medium text-[#4C4C4C]"
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M10 13L5 8L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Back
              </button>

              {/* Small vehicle name */}
              <div className="mt-[8px] flex items-center gap-[5px] pl-[5px]">
                <span className="h-[7px] w-[7px] rounded-full bg-[#222222]" />
                <span className="font-host text-[8px] font-medium leading-none text-[#151515]">
                  {selectedVehicle.name}
                </span>
              </div>

              {/* Vertical share controls */}
              <div className="absolute right-[30px] top-[82px] z-30 flex flex-col items-center">
                <span className="font-poppins text-[6px] font-medium uppercase tracking-[0.08em] text-[#A2A2A2]">
                  Share
                </span>

                <div className="mt-[8px] flex flex-col gap-[7px]">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Share on Facebook"
                    className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M9 8H7v3h2v9h4v-9h3.6l.4-3H13V6c0-1 .5-2 2-2h2V0h-3c-3.3 0-6 2.7-6 6v2z" />
                    </svg>
                  </a>

                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Share on Twitter"
                    className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M22 5.9c-.7.3-1.5.5-2.3.6.8-.5 1.4-1.2 1.7-2.1-.8.5-1.6.8-2.5 1A4 4 0 0 0 12 8.1c0 .3 0 .6.1.9-3.3-.2-6.2-1.7-8.2-4.1-.4.6-.5 1.3-.5 2 0 1.4.7 2.7 1.8 3.4-.7 0-1.3-.2-1.8-.5 0 2 1.4 3.7 3.3 4.1-.4.1-.7.1-1.1.1-.3 0-.5 0-.8-.1.5 1.7 2.1 2.9 3.9 2.9A8 8 0 0 1 3.7 18c-.3 0-.6 0-1-.1a11.3 11.3 0 0 0 6.1 1.8c7.3 0 11.3-6.1 11.3-11.3v-.5c.8-.6 1.4-1.2 1.9-2z" />
                    </svg>
                  </a>

                  <a
                    href="https://pinterest.com"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Share on Pinterest"
                    className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 0C5.4 0 0 5.4 0 12c0 5 3.1 9.3 7.6 11-.1-.9-.2-2.4 0-3.4.2-.9 1.4-6.1 1.4-6.1s-.4-.7-.4-1.8c0-1.7 1-2.9 2.2-2.9 1 0 1.5.8 1.5 1.7 0 1-.7 2.6-1 4-.3 1.2.6 2.2 1.8 2.2 2.1 0 3.7-2.2 3.7-5.4 0-2.8-2-4.8-4.9-4.8-3.3 0-5.3 2.5-5.3 5.1 0 1 .4 2.1.9 2.7.1.1.1.2.1.4-.1.4-.3 1.2-.3 1.4-.1.5-.5.6-.9.4-3.2-1.5-5.2-6.1-5.2-9.8C3.2 5.2 6.9 2 12.5 2c4.9 0 8.7 3.5 8.7 8.2 0 4.9-3.1 8.8-7.3 8.8-1.4 0-2.8-.7-3.2-1.6l-.9 3.3c-.3 1.2-1.2 2.7-1.8 3.6 1.3.4 2.7.6 4.1.6 6.6 0 12-5.4 12-12S18.6 0 12 0z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Vehicle hero */}
              <section className="relative mt-[13px] h-[174px] w-full">
                <img
                  src="/assets/icons/fleetdetailpagesvg.svg"
                  alt=""
                  className="absolute bottom-0 left-[2%] h-[98px] w-[95%] object-fill"
                />

                <div className="absolute left-[27.5%] top-[2px] z-[4] inline-flex items-center gap-[4px] rounded-[7px] bg-[#9EB16F] px-[10px] py-[4px]">
                  <span className="h-[5px] w-[5px] rounded-full bg-[#D0D0D0]" />
                  <span className="font-host text-[7px] font-medium leading-none text-[#151515]">
                    {selectedVehicle.category}
                  </span>
                </div>

                <img
                  src={selectedVehicle.image}
                  alt={selectedVehicle.name}
                  className={`absolute left-1/2 z-10 -translate-x-1/2 object-contain drop-shadow-[0_12px_12px_rgba(0,0,0,0.22)] ${
                    selectedVehicle.name === 'VW Passat'
                      ? 'top-[-24px] h-[218px] w-[218px]'
                      : selectedVehicle.name.toLowerCase().includes('vito')
                        ? 'top-[-18px] h-[235px] w-[235px]'
                        : selectedVehicle.category.toLowerCase().includes('minibus') ||
                            selectedVehicle.image.toLowerCase().includes('fleet-minibus')
                          ? 'top-[-23px] h-[250px] w-[250px]'
                          : 'top-[-24px] h-[220px] w-[220px]'
                  }`}
                />

                <div className="absolute bottom-[8px] left-1/2 z-20 flex -translate-x-1/2 items-start gap-[10px]">
                  <div className="flex flex-col items-center">
                    <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-white">
                      <img
                        src="/assets/images/icon-adult.png"
                        alt=""
                        className="h-[12px] w-[12px] object-contain"
                      />
                    </div>
                    <span className="mt-[2px] whitespace-nowrap font-host text-[6px] font-medium leading-none text-[#2E2E2E]">
                      {detailPassengers} passengers
                    </span>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-white">
                      <img
                        src="/assets/images/icon-luggage.png"
                        alt=""
                        className="h-[12px] w-[12px] object-contain"
                      />
                    </div>
                    <span className="mt-[2px] whitespace-nowrap font-host text-[6px] font-medium leading-none text-[#2E2E2E]">
                      {detailSuitcases} Suitcases
                    </span>
                  </div>
                </div>
              </section>

              {/* Overview */}
              <section className="mt-[34px]">
                <h2 className="font-host text-[26px] font-semibold leading-[1.12] tracking-[-0.025em] text-[#242424]">
                  Vehicle Overview
                </h2>
                <p className="mt-[23px] font-host text-[17px] font-normal leading-[1.6] text-[#171717]">
                  {getMobileVehicleOverview(
                    selectedVehicle.category,
                    selectedVehicle.name,
                  )}
                </p>
              </section>

              {/* What's Included */}
              <section className="mt-[18px]">
                <h2 className="font-host text-[26px] font-semibold leading-[1.12] tracking-[-0.025em] text-[#050505]">
                  What's Included
                </h2>

                <ul className="mt-[28px] flex flex-col gap-[22px]">
                  {whatsIncludedItems.map((item) => (
                    <li key={item} className="flex items-center gap-[12px]">
                      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#E1E1E1] bg-white text-[#75E75B]">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M13.3 4.3L6 11.6L2.7 8.3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span className="font-host text-[20px] font-normal leading-[1.25] text-[#171717]">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Keep the existing booking/specification functionality below the reference section */}
              <section className="mt-12">
                <div className="flex min-h-[82px] items-center justify-between gap-4 rounded-[15px] bg-[#F1F1F1] px-5 py-3">
                  <div>
                    <span className="block font-host text-[25px] font-semibold leading-none text-[#171717]">
                      €{selectedVehicle.price}
                    </span>
                    <span className="mt-2 block font-host text-[14px] text-[#171717]">
                      {selectedVehicle.name}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const vehicleId = selectedVehicle.name
                        .toLowerCase()
                        .replace('mercedes ', '')
                        .replace('vw ', '')
                        .replace('renault ', '')
                        .replace(' ', '-')

                      navigate(
                        `/booking?serviceType=fleet&serviceId=${selectedVehicle.id}&vehicle=${selectedVehicle.id}`
                      )
                    }}
                    className="flex min-h-[54px] items-center justify-center rounded-[14px] bg-[#FF6B2C] px-5 font-host text-[14px] font-semibold text-black"
                  >
                    Book This Vehicle
                  </button>
                </div>

                <div className="mt-7 rounded-[24px] bg-[#F6F6F6] px-6 py-6">
                  <h3 className="font-host text-[19px] font-semibold text-[#171717]">
                    Vehicle Specifications
                  </h3>

                  <div className="mt-5">
                    {[
                      ['Vehicle Type', selectedVehicle.category],
                      ['Model', `${selectedVehicle.name} or Similar`],
                      ['Passengers', `Up to ${detailPassengers}`],
                      ['Suitcases', `${specificationSuitcases} Large Bags`],
                      ['Carry-on Bags', '3 Bags'],
                      ['Air Conditioning', 'Included'],
                      ['WiFi', 'Available'],
                      ['Child Seats', 'On Request'],
                      ['Driver', 'Professional Chauffeur'],
                    ].map(([label, value], index, array) => (
                      <div
                        key={label}
                        className={`flex items-center justify-between gap-5 py-[12px] font-host text-[14px] ${
                          index < array.length - 1 ? 'border-b border-[#C8C8C8]' : ''
                        }`}
                      >
                        <span className="text-[#4B4B4B]">{label}</span>
                        <span className="text-right font-medium text-[#171717]">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </main>
          </div>

          {/* ================= TABLET / DESKTOP DETAIL VIEW ================= */}
          <div className="hidden flex-1 flex-col sm:flex">
          {/* Header */}
          <div className="relative z-20 bg-white px-4">
            <Header />
          </div>

          <main className="relative mx-auto w-full max-w-[1560px] flex-1 px-5 pb-24 pt-4 sm:px-10 lg:px-[70px]">
            {/* Back button and vehicle title */}
            <div className="flex items-center gap-12 sm:gap-14">
              <button
                type="button"
                onClick={() => setSelectedVehicle(null)}
                className="flex h-[52px] items-center gap-2 rounded-full border border-[#D8D8D8] bg-white px-6 font-host text-[16px] font-medium text-[#4C4C4C] transition-colors duration-200 hover:bg-[#F7F7F7]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M10 13L5 8L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Back
              </button>

              <div className="flex items-center">
                <h1 className="font-host text-[22px] font-semibold text-[#151515] sm:text-[24px]">
                  {selectedVehicle.name}
                </h1>
              </div>
            </div>

            {/* Desktop share controls */}
            <div className="absolute right-[28px] top-[18px] z-30 hidden flex-col items-center lg:flex">
              <span className="font-poppins text-[13px] font-medium uppercase tracking-[0.08em] text-[#A2A2A2]">
                Share
              </span>

              <div className="mt-5 flex flex-col gap-6">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Share on Facebook"
                  className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019] transition-transform duration-200 hover:scale-105"
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M9 8H7v3h2v9h4v-9h3.6l.4-3H13V6c0-1 .5-2 2-2h2V0h-3c-3.3 0-6 2.7-6 6v2z" />
                  </svg>
                </a>

                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Share on Twitter"
                  className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019] transition-transform duration-200 hover:scale-105"
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M22 5.9c-.7.3-1.5.5-2.3.6.8-.5 1.4-1.2 1.7-2.1-.8.5-1.6.8-2.5 1A4 4 0 0 0 12 8.1c0 .3 0 .6.1.9-3.3-.2-6.2-1.7-8.2-4.1-.4.6-.5 1.3-.5 2 0 1.4.7 2.7 1.8 3.4-.7 0-1.3-.2-1.8-.5 0 2 1.4 3.7 3.3 4.1-.4.1-.7.1-1.1.1-.3 0-.5 0-.8-.1.5 1.7 2.1 2.9 3.9 2.9A8 8 0 0 1 3.7 18c-.3 0-.6 0-1-.1a11.3 11.3 0 0 0 6.1 1.8c7.3 0 11.3-6.1 11.3-11.3v-.5c.8-.6 1.4-1.2 1.9-2z" />
                  </svg>
                </a>

                <a
                  href="https://pinterest.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Share on Pinterest"
                  className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019] transition-transform duration-200 hover:scale-105"
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 0C5.4 0 0 5.4 0 12c0 5 3.1 9.3 7.6 11-.1-.9-.2-2.4 0-3.4.2-.9 1.4-6.1 1.4-6.1s-.4-.7-.4-1.8c0-1.7 1-2.9 2.2-2.9 1 0 1.5.8 1.5 1.7 0 1-.7 2.6-1 4-.3 1.2.6 2.2 1.8 2.2 2.1 0 3.7-2.2 3.7-5.4 0-2.8-2-4.8-4.9-4.8-3.3 0-5.3 2.5-5.3 5.1 0 1 .4 2.1.9 2.7.1.1.1.2.1.4-.1.4-.3 1.2-.3 1.4-.1.5-.5.6-.9.4-3.2-1.5-5.2-6.1-5.2-9.8C3.2 5.2 6.9 2 12.5 2c4.9 0 8.7 3.5 8.7 8.2 0 4.9-3.1 8.8-7.3 8.8-1.4 0-2.8-.7-3.2-1.6l-.9 3.3c-.3 1.2-1.2 2.7-1.8 3.6 1.3.4 2.7.6 4.1.6 6.6 0 12-5.4 12-12S18.6 0 12 0z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Vehicle showcase */}
            <section className="relative mt-6 h-[500px] w-full lg:pr-[52px]">
              <img
                src="/assets/icons/fleetdetailpagesvg.svg"
                alt=""
                className="absolute bottom-0 left-[4%] h-[275px] w-[96%] object-fill"
              />

              <div className="absolute bottom-[218px] left-[11%] z-2 inline-flex items-center gap-2 rounded-[12px] bg-[#9EB16F] px-4 py-2">
                <span className="h-[11px] w-[11px] rounded-full bg-[#D0D0D0]" />

                <span className="font-host text-[13px] font-medium text-[#151515]">
                  {selectedVehicle.category}
                </span>
              </div>

              <img
                src={selectedVehicle.image}
                alt={selectedVehicle.name}
                className={`absolute left-1/2 z-10 -translate-x-1/2 object-contain drop-shadow-[0_22px_22px_rgba(0,0,0,0.24)] ${
                  selectedVehicle.name === 'VW Passat'
                    ? 'top-[15px] w-auto max-h-[390px] max-w-[74%] scale-[1.1]'
                    : selectedVehicle.name.toLowerCase().includes('vito')
                      ? 'top-[70px] h-[300px] w-[560px] max-w-[70%]'
                      : selectedVehicle.category.toLowerCase().includes('minibus') ||
                          selectedVehicle.image.toLowerCase().includes('fleet-minibus')
                        ? 'top-[55px] h-[320px] w-[650px] max-w-[78%]'
                        : 'top-[15px] w-auto max-h-[365px] max-w-[74%]'
                }`}
              />

              <div className="absolute bottom-[18px] left-1/2 z-2 flex -translate-x-1/2 items-start gap-6">
                <div className="flex flex-col items-center">
                  <div className="flex h-[78px] w-[78px] items-center justify-center rounded-full bg-white">
                    <img
                      src="/assets/images/icon-adult.png"
                      alt=""
                      className="h-[30px] w-[30px] object-contain"
                    />
                  </div>

                  <span className="mt-2 whitespace-nowrap font-host text-[14px] font-medium text-[#2E2E2E]">
                    {detailPassengers} passengers
                  </span>
                </div>

                <div className="flex flex-col items-center">
                  <div className="flex h-[78px] w-[78px] items-center justify-center rounded-full bg-white">
                    <img
                      src="/assets/images/icon-child.png"
                      alt=""
                      className="h-[30px] w-[30px] object-contain"
                    />
                  </div>

                  <span className="mt-2 whitespace-nowrap font-host text-[14px] font-medium text-[#2E2E2E]">
                    {detailSuitcases} Suitcases
                  </span>
                </div>
              </div>
            </section>

            {/* Mobile share controls */}
            <div className="mt-7 flex items-center justify-center gap-4 lg:hidden">
              <span className="font-poppins text-[11px] font-medium uppercase tracking-[0.08em] text-[#A2A2A2]">
                Share
              </span>

              <div className="flex gap-3">
                <a
                  href="https://facebook.com"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                >
                  f
                </a>
                <a
                  href="https://twitter.com"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                >
                  t
                </a>
                <a
                  href="https://pinterest.com"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F2F3] text-[#9B4019]"
                >
                  p
                </a>
              </div>
            </div>

            {/* Overview and specifications */}
            <div className="mt-16 grid grid-cols-1 items-start gap-12 lg:grid-cols-[1.12fr_1fr] lg:gap-[88px]">
              <div>
                <section>
                  <h2 className="font-host text-[19px] font-semibold text-[#171717]">
                    Vehicle Overview
                  </h2>

                  <p className="mt-4 max-w-[680px] font-host text-[17px] leading-[1.65] text-[#171717]">
                    {getVehicleOverview(
                      selectedVehicle.category,
                      selectedVehicle.name,
                      selectedVehicle.description,
                    )}
                  </p>
                </section>

                <section className="mt-10">
                  <h2 className="font-host text-[19px] font-semibold text-[#171717]">
                    What's Included
                  </h2>

                  <ul className="mt-6 flex flex-col gap-4">
                    {whatsIncludedItems.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-3"
                      >
                        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#E1E1E1] bg-white text-[#89D95A]">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M13.3 4.3L6 11.6L2.7 8.3"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>

                        <span className="font-host text-[17px] text-[#171717]">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <div>
                {/* Price and booking */}
                <div className="flex min-h-[82px] items-center justify-between gap-5 rounded-[15px] bg-[#F1F1F1] px-6 py-3">
                  <div>
                    <span className="block font-host text-[28px] font-semibold leading-none text-[#171717]">
                      €{selectedVehicle.price}
                    </span>

                    <span className="mt-2 block font-host text-[16px] text-[#171717]">
                      {selectedVehicle.name}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const vehicleId = selectedVehicle.name
                        .toLowerCase()
                        .replace('mercedes ', '')
                        .replace('vw ', '')
                        .replace('renault ', '')
                        .replace(' ', '-')

                      navigate(
                        `/booking?serviceType=fleet&serviceId=${selectedVehicle.id}&vehicle=${selectedVehicle.id}`
                      )
                    }}
                    className="flex min-h-[58px] items-center justify-center gap-3 rounded-[15px] bg-[#FF6B2C] px-8 font-host text-[16px] font-semibold text-black shadow-[0_4px_12px_rgba(255,107,44,0.24)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
                  >
                    <svg
                      width="27"
                      height="17"
                      viewBox="0 0 32 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 13L6.5 7.5C7 6.4 8.1 5.7 9.3 5.7H22.7C23.9 5.7 25 6.4 25.5 7.5L28 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M3 13H29V17H3V13Z"
                        fill="currentColor"
                      />
                      <circle cx="8" cy="17" r="2.5" fill="currentColor" />
                      <circle cx="24" cy="17" r="2.5" fill="currentColor" />
                    </svg>

                    Book This Vehicle
                  </button>
                </div>

                {/* Specifications */}
                <div className="mt-7 rounded-[28px] bg-[#F6F6F6] px-8 py-7">
                  <h3 className="font-host text-[19px] font-semibold text-[#171717]">
                    Vehicle Specifications
                  </h3>

                  <div className="mt-5">
                    {[
                      ['Vehicle Type', selectedVehicle.category],
                      ['Model', `${selectedVehicle.name} or Similar`],
                      ['Passengers', `Up to ${detailPassengers}`],
                      [
                        'Suitcases',
                        `${specificationSuitcases} Large Bags`,
                      ],
                      ['Carry-on Bags', '3 Bags'],
                      ['Air Conditioning', 'Included'],
                      ['WiFi', 'Available'],
                      ['Child Seats', 'On Request'],
                      ['Driver', 'Professional Chauffeur'],
                    ].map(([label, value], index, array) => (
                      <div
                        key={label}
                        className={`flex items-center justify-between gap-6 py-[13px] font-host text-[15px] ${
                          index < array.length - 1
                            ? 'border-b border-[#C8C8C8]'
                            : ''
                        }`}
                      >
                        <span className="text-[#4B4B4B]">
                          {label}
                        </span>

                        <span className="text-right font-medium text-[#171717]">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Explore fleet carousel */}
            <section className="mt-16">
              <div className="flex items-center justify-between">
                <h2 className="font-host text-[20px] font-semibold text-[#171717]">
                  Explore Our Fleet
                </h2>
                
                <div className="hidden lg:flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => scrollCarousel('left')}
                    aria-label="Scroll left"
                    className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-white text-[#FF6B2C] shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-[#F0F0F0] transition-all duration-300 hover:scale-105 hover:bg-[#FF6B2C] hover:text-white"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollCarousel('right')}
                    aria-label="Scroll right"
                    className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-white text-[#FF6B2C] shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-[#F0F0F0] transition-all duration-300 hover:scale-105 hover:bg-[#FF6B2C] hover:text-white"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="relative mt-6">
                <div 
                  ref={carouselRef}
                  className="flex w-full gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 scroll-smooth"
                >
                  {otherVehicles.map((vehicle) => (
                    <div
                      key={vehicle.name}
                      className="w-[280px] shrink-0 snap-center sm:w-[340px] lg:w-[360px]"
                    >
                      <FleetVehicleCard
                        {...vehicle}
                        variant="compact"
                        onClick={() => setSelectedVehicle(vehicle)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </main>
        </div>
        </>
      ) : (
        /* ================= GRID LIST VIEW ================= */
        <div className="flex flex-col flex-1">
        
          <section
            className="
              relative
              h-[300px]
              w-full
              overflow-hidden
              bg-[#D8F0FA]

              md:h-auto
            "
          >
            <img
              src="/assets/heroImages/fleethero.png"
              alt="Salzburg private vehicle journey"
              className="
                h-full
                w-full
                object-cover
                object-center

                md:block
                md:h-auto
                md:w-full
                md:object-contain
              "
            />

            <div className="absolute inset-0 bg-[#3570E9]/5 mix-blend-overlay" />

            <div className="absolute inset-x-0 top-0 z-20 px-4">
              <Header />
            </div>
          </section>

          {/* Main Content Area */}
          <main className="mx-auto w-full max-w-[1280px] flex-1 px-[18px] pb-24 pt-12 sm:px-6 lg:px-[42px] lg:pb-28 lg:pt-14">
            {/* Section Heading */}
            <h2 className="text-center font-host text-[30px] font-semibold leading-[1.15] tracking-[-0.025em] text-[#BDBDBD] sm:text-[36px] lg:text-[42px]">
              Choose the Perfect Vehicle for Your Journey
            </h2>

            {/* Filters Dropdown Selectors */}
            <div className="mt-9 flex flex-col items-stretch justify-center gap-6 sm:flex-row">
              <Dropdown
                label="Vehicle Type"
                value={selectedType}
                options={vehicleTypes}
                onChange={setSelectedType}
              />
              <Dropdown
                label="VEHICLE MAKE"
                value={selectedMake}
                options={vehicleMakes}
                onChange={setSelectedMake}
              />
            </div>

            {/* Vehicle Grid Listing */}
            <div className="mt-8">
              {filteredVehicles.length > 0 ? (
                <div className="grid auto-rows-fr gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredVehicles.map((vehicle) => (
                    <FleetVehicleCard
                      key={vehicle.name}
                      {...vehicle}
                      onClick={() => setSelectedVehicle(vehicle)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 border border-dashed border-slate-200 rounded-[24px] bg-slate-50/50">
                  <p className="font-host text-[16px] text-slate-500">
                    No vehicles match your search criteria.
                  </p>
                  <button
                    onClick={() => {
                      setSelectedType('Any Type')
                      setSelectedMake('Any Make')
                    }}
                    className="mt-4 font-host text-[14px] font-semibold text-[#FF7A29] hover:underline"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </div>
          </main>
        </div>
      )}

      {/* Footer Wrapper */}
      <Footer />
    </div>
  )
}